import { useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Logo } from './Logo';
import { BillingToggle } from './BillingToggle';
import { PlanButton, PlanCard } from './PlanCard';
import { PlanErrorSummary } from './PlanErrorSummary';
import {
  PLAN_DETAILS,
  PLAN_IDS,
  TRIAL_DAYS,
  formatBillingDate,
  formatPrice,
  getPlanPrice,
  getTrialEndDate,
} from '../utils/planPricing';
import { billingErrorMessage, startCheckout } from '../utils/planActions';

// Sign-up step 1 of 3. The trial gives every feature, Family sharing included, whatever plan
// someone later picks (decided 2026-10-10), so there is ONE trial button rather than one per
// plan. The cards show what each plan costs afterwards, with a secondary "Subscribe" button for
// people who are already sure. No skip: the trial needs no card, so starting is low commitment.
//
// What the trial button does is up to the caller (onChooseTrial, see OnboardingPlanPage): in a
// fresh sign-up it only records the choice and the trial starts when onboarding finishes; for an
// existing owner with no plan it starts the trial straight away. inOnboarding=false also hides the
// welcome line and step indicator, since that owner isn't in the 3-step flow.
export function OnboardingPlan({ inOnboarding = true, onChooseTrial }) {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [trialBusy, setTrialBusy] = useState(false);
  const [busyPlan, setBusyPlan] = useState('');
  const [error, setError] = useState('');
  // Back from Stripe Checkout without paying (its "back" link adds ?checkout=cancelled).
  const [searchParams] = useSearchParams();
  const [showCancelled, setShowCancelled] = useState(searchParams.get('checkout') === 'cancelled');
  const trialHintId = useId();
  const plansHeadingId = useId();

  const changeInterval = (next) => {
    setBillingInterval(next);
  };

  const handleStartTrial = async () => {
    setError('');
    setShowCancelled(false);
    setTrialBusy(true);
    try {
      await onChooseTrial();
    } catch (err) {
      console.warn('Failed to start trial from plan screen:', err);
      // 409: this account can't have a trial (e.g. it already had one), so retrying won't help.
      setError(
        err?.status === 409
          ? "A free trial isn't available for this account. You can subscribe to a plan below."
          : "We couldn't start your free trial. Please try again."
      );
      setTrialBusy(false);
    }
  };

  const handleSubscribe = async (plan) => {
    setError('');
    setShowCancelled(false);
    setBusyPlan(plan);
    try {
      // Leaves for Stripe; the button stays busy until the page changes.
      await startCheckout({ plan, interval: billingInterval, returnTo: 'onboarding' });
    } catch (err) {
      console.warn('Failed to start checkout from sign-up:', err);
      setError(billingErrorMessage(err, "We couldn't open checkout. Please try again, or start your free trial instead."));
      setBusyPlan('');
    }
  };

  // Subscribing up front still gets the full free trial (decided 2026-10-10), so the first
  // charge is when the trial would end. State that date plainly wherever money is mentioned.
  const firstChargeDate = formatBillingDate(getTrialEndDate());
  const renewsEvery = billingInterval === 'yearly' ? 'year' : 'month';

  return (
    <div className="min-h-screen bg-[var(--plan-page-bg)] px-4 py-10 sm:px-6">
      <main className="mx-auto flex w-full max-w-[960px] flex-col items-center text-center">
        <div aria-hidden="true">
          <Logo className="mx-auto h-14 w-14" color="#AFBB8B" />
        </div>
        {inOnboarding && (
          <>
            <p className="mt-2 text-3xl font-bold text-[#6B7A54]">Welcome to MealPlan Genie</p>
            <div className="mt-3 flex items-center justify-center gap-2" aria-hidden="true">
              <div className="h-2 w-2 rounded-full bg-[var(--plan-button)]"></div>
              <div className="h-2 w-2 rounded-full bg-gray-300"></div>
              <div className="h-2 w-2 rounded-full bg-gray-300"></div>
            </div>
          </>
        )}

        <h1 className="mt-8 text-3xl font-bold text-[color:var(--plan-heading-text)] sm:text-4xl">
          Choose how you'd like to start
        </h1>
        <p className="mt-3 max-w-xl text-lg text-[color:var(--plan-muted-text)]">
          Try every feature free for {TRIAL_DAYS} days, including sharing with your household. Or subscribe now if
          you already know it's for you.
        </p>

        <div className="mt-8 w-full max-w-xl">
          <PlanErrorSummary message={error} />
          <div role="status">
            {showCancelled && (
              <p className="mb-6 rounded-2xl border border-[color:var(--plan-card-border)] bg-white px-5 py-4 text-base text-[color:var(--plan-heading-text)]">
                Checkout was cancelled, so nothing was charged. Start your free trial or subscribe whenever you&apos;re ready.
              </p>
            )}
          </div>
        </div>

        <div className="w-full max-w-sm">
          <PlanButton busy={trialBusy} onClick={handleStartTrial} aria-describedby={trialHintId}>
            Start {TRIAL_DAYS}-day free trial
          </PlanButton>
          <p id={trialHintId} className="mt-3 text-base text-[color:var(--plan-muted-text)]">
            No card needed. Choose a plan any time before your trial ends.
          </p>
        </div>

        <section aria-labelledby={plansHeadingId} className="mt-12 flex w-full flex-col items-center">
          <h2 id={plansHeadingId} className="text-2xl font-bold text-[color:var(--plan-heading-text)]">
            Plans after your trial
          </h2>
          <div className="mt-5 flex w-full justify-center">
            <BillingToggle value={billingInterval} onChange={changeInterval} />
          </div>

          {/* gap-10 leaves room for the "Most popular" badge, which sits over the card's top edge. */}
          <div className="mt-10 flex w-full flex-wrap gap-10 text-left md:gap-8">
            {PLAN_IDS.map((plan) => (
              <PlanCard
                key={plan}
                plan={plan}
                interval={billingInterval}
                context="signup"
                headingLevel={3}
                badge={plan === 'family' ? 'Most popular' : undefined}
                actionLabel={`Subscribe to ${PLAN_DETAILS[plan].name}`}
                actionVariant="secondary"
                onAction={() => handleSubscribe(plan)}
                busy={busyPlan === plan}
                hint={`First charge ${formatPrice(getPlanPrice(plan, billingInterval))} on ${firstChargeDate}, after your ${TRIAL_DAYS} free days. Secure checkout with Stripe.`}
              />
            ))}
          </div>
        </section>

        <p className="mt-10 w-full max-w-[480px] rounded-2xl border border-[color:var(--plan-card-border)] bg-white px-6 py-4 text-base text-[color:var(--plan-muted-text)]">
          Nothing is charged during your free trial. If you subscribe now, your {TRIAL_DAYS} free days still apply:
          your first charge is on {firstChargeDate}, then your plan renews every {renewsEvery} at the price shown.
          You can change plans later in Plan &amp; billing.
        </p>

        {inOnboarding && <p className="mt-8 text-sm text-[color:var(--plan-muted-text)]">Step 1 of 3</p>}
      </main>
    </div>
  );
}
